import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from '../../store/slices/authSlice';

/** An error toast raised by a failed API write in this session. Kept in memory only. */
export interface FailedAction {
    id: string;
    message: string;
    at: string;
    read: boolean;
    retryLabel: string | null;
}

const MAX = 20;

/** Retry callbacks are functions, so they live outside the (serialisable) store, by id. */
const retries = new Map<string, () => void>();
let seq = 0;

export const retryFor = (id: string): (() => void) | undefined => retries.get(id);

const slice = createSlice({
    name: 'failedActions',
    initialState: [] as FailedAction[],
    reducers: {
        failed: {
            reducer: (state, action: PayloadAction<FailedAction>) => {
                state.unshift(action.payload);
                for (const dropped of state.splice(MAX)) retries.delete(dropped.id);
            },
            prepare: (message: string, retry?: { label: string; run: () => void }) => {
                seq += 1;
                const id = `failed:${Date.now()}:${seq}`;
                if (retry) retries.set(id, retry.run);
                return { payload: { id, message, at: new Date().toISOString(), read: false, retryLabel: retry?.label ?? null } };
            },
        },
        dismissed: (state, action: PayloadAction<string>) => {
            retries.delete(action.payload);
            return state.filter((f) => f.id !== action.payload);
        },
        allRead: (state) => {
            for (const f of state) f.read = true;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => {
            retries.clear();
            return [];
        });
    },
});

export const { failed, dismissed, allRead } = slice.actions;
export default slice.reducer;
